import { sendEventDirectlyToHandler } from './api';
import { getPreciseRelativeTime } from './utils';
var log = function () { };
var kpiLogger = { recordKpiUsage: function () { }, recordKpiSuccess: function () { }, recordKpiFailure: function () { } };
var worker;
var workerLoadFromBlob = true;
var batchingIsEnabled = true;
var numEventsToTriggerUpload = 10;
var workerCreateDuration = 0;
var workerInitStart = 0;
var workerInstallationSucceeded = false;
var protocolVersion = '1';
var stats = {
    processed: 0,
    eventsProcessingTime: 0,
    averageProcessingTimeCostPerEvent: 0,
    batches: 0,
    miscEvents: 0,
    sessionDuration: 0,
    // Worker Perf Timings
    workerInstallationSucceeded: false,
    workerInitDuration: 0,
    workerFullInitDuration: 0,
    // Initialization overhead
    startOtelWorkerMethodScriptSharpDuration: 0,
    startOtelWorkerMethodTypescriptDuration: 0,
    startOtelWorkerMethodDuration: 0,
    // Communication with webworker reliability stats
    sentMessageCounter: 0,
    receivedMessageCounter: 0
};
var workerEnabled = false;
var workerInitialized = false;
var workerInitializationFailed = false;
var shutdownCalled = false;
var periodicFlusherTimerId = 0;
var periodicPingTimerId = 0;
var asyncFlushTimerScheduled = false;
var queue = [];
var sessionStartTime = new Date().getTime();
export function startOtelWorker(settings) {
    var _a, _b;
    var startOtelWorkerMethodStart = getPreciseRelativeTime();
    try {
        if (!settings.workerUrl) {
            return { succeeded: false, failureReason: 'Missing Worker script url!' };
        }
        if (!settings.batchingSettings) {
            return { succeeded: false, failureReason: 'Missing Batching settings!' };
        }
        if (!settings.healthKpiMethods) {
            return { succeeded: false, failureReason: 'Missing Health KPI methods!' };
        }
        if (!settings.ulsLoggingMethods || !settings.ulsLoggingMethods.sendTraceTag) {
            return { succeeded: false, failureReason: 'Missing ULS logging methods!' };
        }
        if (workerInitialized) {
            return { succeeded: false, failureReason: 'Worker has already been initialized!' };
        }
        if (workerInitializationFailed) {
            return { succeeded: false, failureReason: 'Worker has already attempted initialization and failed!' };
        }
        workerInitialized = true;
        setOtelWorkerEnabledState(true);
        log = settings.ulsLoggingMethods.sendTraceTag;
        kpiLogger = settings.healthKpiMethods;
        workerLoadFromBlob = settings.loadFromBlob;
        batchingIsEnabled = settings.batchingSettings.enabled;
        numEventsToTriggerUpload = settings.batchingSettings.numEventsToTriggerUpload || 10;
        var timeToWaitBeforeAutoUploadingEvents = settings.batchingSettings.timeToWaitBeforeAutoUploadingEvents || 10 * 1000;
        periodicFlusherTimerId = self.setInterval(function otelWorkerPeriodicEventFlusher() {
            flush();
        }, timeToWaitBeforeAutoUploadingEvents);
        var periodicPingFrequency = settings.batchingSettings.periodicPingFrequency || 3 * 1000;
        periodicPingTimerId = self.setInterval(function otelWorkerPeriodicPing() {
            sendPingMessageToWorker();
        }, periodicPingFrequency);
        log(0x22052846 /* tag_8bs7g */, 227 /* msoulscat_MSOSP_OTelJSWebWorkerPrototype */, 50 /* Info */, "Installing OTel worker with settings " + getSettingsStringForLogging(settings));
        kpiLogger.recordKpiUsage();
        var createWorkerStart = getPreciseRelativeTime();
        if (workerLoadFromBlob) {
            var workerBlob = new Blob([
                // Log installation failures as a warning under tag_8bs7h
                "try{importScripts('" + encodeURI(settings.workerUrl) + "');}catch(e){self.postMessage({tagId:570763335,level:15,message:'Failed to load worker script: '+e})}"
            ], {
                type: 'application/javascript'
            });
            worker = new Worker(URL.createObjectURL(workerBlob));
        }
        else {
            worker = new Worker(settings.workerUrl);
        }
        worker.onmessage = onWebWorkerMessage.bind(this);
        workerCreateDuration = getPreciseRelativeTime() - createWorkerStart;
        var workerInitStartTime = new Date().getTime();
        workerInitStart = getPreciseRelativeTime();
        var settingsEvent = {
            kind: 'initialize',
            timestamp: workerInitStartTime,
            event: settings.telemetryLoggerSettings,
            workerSettings: { flushOnEveryMessage: settings.flushOnEveryMessage }
        };
        sendToWorkerInternal([settingsEvent]);
        // There are some critical events that get logged during boot that we want to send as soon as possible (after we have initialized the worker)
        flushAsync();
        log(0x22052848 /* tag_8bs7i */, 227 /* msoulscat_MSOSP_OTelJSWebWorkerPrototype */, 50 /* Info */, "Created a Worker with url: " + settings.workerUrl);
    }
    catch (e) {
        var errorName = 'UnexpectedException';
        var extendedProperties = new Map();
        if (typeof e === 'string') {
            extendedProperties.set('ErrorName', e);
            // Most of the errors that we are getting here are 'security errors', so let's classify those separately from the rest
            if (e.indexOf('SecurityError') !== -1) {
                errorName = 'SecurityError';
            }
        }
        kpiLogger.recordKpiFailure(errorName, extendedProperties);
        shutdown();
        workerInitializationFailed = true;
        workerInitialized = false;
        return { succeeded: false, failureReason: "Failed to install the worker: " + (e ? e : 'unknown reason') };
    }
    finally {
        sessionStartTime = ((_a = settings.appData) === null || _a === void 0 ? void 0 : _a.sessionStartTime) || sessionStartTime;
        stats.startOtelWorkerMethodScriptSharpDuration = ((_b = settings.appData) === null || _b === void 0 ? void 0 : _b.startOtelWorkerMethodScriptSharpDuration) || 0;
        stats.startOtelWorkerMethodTypescriptDuration = getPreciseRelativeTime() - startOtelWorkerMethodStart;
    }
    return { succeeded: true };
}
function onWebWorkerMessage(event) {
    try {
        var ulsEvent = event.data;
        switch (ulsEvent.tagId) {
            case 0x2205284b /* tag_8bs7l */: {
                var workerInitDuration = ulsEvent.message;
                var fullInitWorkerDuration = getPreciseRelativeTime() - workerInitStart;
                var completeWorkerInitDuration = workerCreateDuration + fullInitWorkerDuration;
                // Fix up the uls message to be more user friendly
                ulsEvent.message = "Successfully installed the OTel worker: CompleteWorkerInitDuration=" + completeWorkerInitDuration + ", CreateWorkerDuration=" + workerCreateDuration + ", WorkerInitDuration=" + workerInitDuration + ", FullInitWorkerDuration=" + fullInitWorkerDuration;
                var prop = new Map();
                // Time it took to create a worker blob URL and call `new Worker(blobUrl)` in the main thread
                prop.set('CreateWorkerDuration', workerCreateDuration.toString());
                // How long it took for our 'init' message to go from the main thread -> worker
                prop.set('WorkerInitDuration', workerInitDuration.toString());
                // How long it took for our 'init' message to go from the main thread -> worker -> main thread
                prop.set('FullInitWorkerDuration', fullInitWorkerDuration.toString());
                // Time to create a worker plus the time to send the 'init' message and have it go from thread -> worker -> main thread
                prop.set('CompleteWorkerInitDuration', completeWorkerInitDuration.toString());
                // Did we load the worker via a blob URL? Or via a script URL?
                prop.set('LoadFromBlob', workerLoadFromBlob.toString());
                kpiLogger.recordKpiSuccess(Math.round(fullInitWorkerDuration), prop);
                stats.workerInstallationSucceeded = true;
                stats.workerInitDuration = workerInitDuration;
                stats.workerFullInitDuration = fullInitWorkerDuration;
                workerInstallationSucceeded = true;
                // Flush any outstanding events to the worker to ensure that events that are logged early in the session get sent before the app shutdown
                flush();
                break;
            }
            case 0x22052847 /* tag_8bs7h */: {
                kpiLogger.recordKpiFailure('FailedToLoadScript', undefined /* extendedProperties */);
                break;
            }
            case 0x22045448 /* tag_8bfri */: {
                // MessageCounter: Message acknowledgements from the worker thread
                stats.receivedMessageCounter += 1;
                break;
            }
            default: {
                break;
            }
        }
        log(ulsEvent.tagId, ulsEvent.category, ulsEvent.level, "[Worker] " + ulsEvent.message);
    }
    catch (ex) {
        log(0x2205284c /* tag_8bs7m */, 227 /* msoulscat_MSOSP_OTelJSWebWorkerPrototype */, 15 /* Warning */, "Failed to process message from the worker: " + ex);
    }
}
function getSettingsStringForLogging(settings) {
    // Don't stringify telemetryLoggerSettings as OTel layer will log those separate (and in a safe manner)
    function replacer(key, value) {
        if (key === 'telemetryLoggerSettings') {
            return undefined;
        }
        return value;
    }
    return JSON.stringify(settings, replacer);
}
export function setOtelWorkerEnabledState(enabled) {
    workerEnabled = enabled;
    if (!workerEnabled) {
        queue = [];
    }
}
// =======================================================================================================
export function sendToWorker(event) {
    if (!workerEnabled || shutdownCalled) {
        return;
    }
    // There is a small number of critical telemetry events that get sent before the worker has a chance to initialize
    if (!workerInitialized) {
        addEventToQueue(event);
        return;
    }
    if (batchingIsEnabled) {
        addEventToQueue(event);
    }
    else {
        sendToWorkerInternal([event]);
    }
    // If we encountered a shutdown event, flush all of the outstanding events to the webworker (including a shutdown one),
    // tear down the periodic timer and log session level telemetry.
    if (event.kind === 'shutdown') {
        shutdown();
    }
}
function addEventToQueue(event) {
    queue.push(event);
    if (queue.length < numEventsToTriggerUpload) {
        return;
    }
    flushAsync();
}
function flushAsync() {
    if (!asyncFlushTimerScheduled) {
        self.setTimeout(function otelWorkerAsyncFlusher() {
            asyncFlushTimerScheduled = false;
            flush();
        }, 0);
        asyncFlushTimerScheduled = true;
    }
}
function flush() {
    if (queue.length <= 0) {
        return;
    }
    sendToWorkerInternal(queue);
    queue = [];
}
// We don't want to count ping messages as part of actual telemetry events as that will dilute performance numbers.
function sendPingMessageToWorker() {
    try {
        var message = {
            events: [],
            seq: stats.sentMessageCounter,
            timestamp: new Date().getTime(),
            pingRequest: true,
            version: protocolVersion
        };
        worker === null || worker === void 0 ? void 0 : worker.postMessage(message);
        stats.sentMessageCounter += 1;
    }
    catch (ex) {
        log(0x2160b096 /* tag_7ylcw */, 227 /* msoulscat_MSOSP_OTelJSWebWorkerPrototype */, 15 /* Warning */, "Failed to send ping message to the worker: " + ex);
    }
}
function sendToWorkerInternal(events) {
    try {
        stats.processed += events.length;
        stats.batches += 1;
        // Debugging aid - makes it easier to know how many 'real' telemetry events were actually sent in the session
        for (var i = 0; i < events.length; i = i + 1) {
            if (events[i].kind === 'initialize' ||
                events[i].kind === 'addNamespaceMapping' ||
                events[i].kind === 'flush' ||
                events[i].kind === 'shutdown') {
                stats.miscEvents += 1;
            }
        }
        var message = {
            events: events,
            seq: stats.sentMessageCounter,
            timestamp: new Date().getTime(),
            version: protocolVersion
        };
        var start = getPreciseRelativeTime();
        worker === null || worker === void 0 ? void 0 : worker.postMessage(message);
        stats.eventsProcessingTime += getPreciseRelativeTime() - start;
        stats.sentMessageCounter += 1;
    }
    catch (ex) {
        log(0x2204b8ce /* tag_8bl9o */, 227 /* msoulscat_MSOSP_OTelJSWebWorkerPrototype */, 15 /* Warning */, "Failed to send message to the worker: " + ex);
    }
}
function computeShutdownQueueStats() {
    // We only send WAC events to Aria, so only record the names of those event types
    var eventNames = '';
    queue.forEach(function (event) {
        if (event.kind === 'action' || event.kind === 'activity' || event.kind === 'event') {
            eventNames = eventNames + event.event.name + ';';
        }
    });
    var numberOfEventsInQueue = queue.length;
    var sessionStartEventPresent = eventNames.indexOf('Online.SessionStart') !== -1;
    var editDocumentEventPresent = eventNames.indexOf('Online.UserAction.EditDocument') !== -1;
    return {
        numberOfEventsInQueue: numberOfEventsInQueue,
        eventNames: eventNames,
        sessionStartEventPresent: sessionStartEventPresent,
        editDocumentEventPresent: editDocumentEventPresent
    };
}
function shutdown() {
    if (!workerInitialized || shutdownCalled) {
        return;
    }
    shutdownCalled = true;
    var shutdownQueueStats = computeShutdownQueueStats();
    try {
        flush();
        if (periodicFlusherTimerId) {
            self.clearInterval(periodicFlusherTimerId);
            periodicFlusherTimerId = 0;
        }
        if (periodicPingTimerId) {
            self.clearInterval(periodicPingTimerId);
            periodicPingTimerId = 0;
        }
        if (!workerInstallationSucceeded) {
            var prop = new Map();
            var totalInitDelay = getPreciseRelativeTime() - workerInitStart;
            // Time it took to create a worker blob URL and call `new Worker(blobUrl)` in the main thread
            prop.set('CreateWorkerDuration', workerCreateDuration.toString());
            // Time passed since we used `worker.postMessage` to send an `init` event
            prop.set('FailedWorkerInitDuration', totalInitDelay.toString());
            prop.set('WasWorkerConstructorCalledSuccessfully', (!!worker).toString());
            kpiLogger.recordKpiFailure('ShutdownBeforeReady', prop);
            log(0x2205284d /* tag_8bs7n */, 227 /* msoulscat_MSOSP_OTelJSWebWorkerPrototype */, 15 /* Warning */, "Failed to install the worker. Waited for " + totalInitDelay + "ms");
        }
        logSessionLevelTelemetry(shutdownQueueStats);
    }
    catch (ex) {
        log(0x2205284f /* tag_8bs7p */, 227 /* msoulscat_MSOSP_OTelJSWebWorkerPrototype */, 15 /* Warning */, "Failed to shutdown otel worker: " + ex);
    }
}
function logSessionLevelTelemetry(shutdownQueueStats) {
    computeSessionAverages();
    logSessionLevelTelemetryViaUls();
    logSessionLevelTelemetryViaOTel(shutdownQueueStats);
}
function logSessionLevelTelemetryViaUls() {
    var msg = JSON.stringify(stats);
    // ASSERTTAG_IGNORE_START
    log(0x2205284e /* tag_8bs7o */, 227 /* msoulscat_MSOSP_OTelJSWebWorkerPrototype */, 50 /* Info */, msg);
    // ASSERTTAG_IGNORE_FINISH
}
function logSessionLevelTelemetryViaOTel(shutdownQueueStats) {
    var telemetryEvent = {
        name: 'Office.Online.Telemetry.OTelJS.WebWorker.Session.Stats',
        dataFields: [
            { name: 'Processed', double: stats.processed },
            { name: 'EventsProcessingTime', double: stats.eventsProcessingTime },
            { name: 'AverageProcessingTimeCostPerEvent', double: stats.averageProcessingTimeCostPerEvent },
            { name: 'Batches', double: stats.batches },
            { name: 'BatchingSettings', double: !batchingIsEnabled ? 0 : numEventsToTriggerUpload },
            { name: 'MiscEvents', double: stats.miscEvents },
            { name: 'SessionDuration', double: stats.sessionDuration },
            // Worker Perf Timings
            { name: 'WorkerInstallationSucceeded', bool: stats.workerInstallationSucceeded },
            { name: 'WorkerInitDuration', double: stats.workerInitDuration },
            { name: 'WorkerFullInitDuration', double: stats.workerFullInitDuration },
            // Initialization overhead
            { name: 'StartOtelWorkerMethodDuration', double: stats.startOtelWorkerMethodDuration },
            { name: 'StartOtelWorkerMethodScriptSharpDuration', double: stats.startOtelWorkerMethodScriptSharpDuration },
            { name: 'StartOtelWorkerMethodTypescriptDuration', double: stats.startOtelWorkerMethodTypescriptDuration },
            // Shutdown stats
            { name: 'NumOfEventsInQueueAtShutdown', double: shutdownQueueStats.numberOfEventsInQueue },
            { name: 'NamesOfEventsInQueueAtShutdown', string: shutdownQueueStats.eventNames },
            { name: 'SessionStartEventPresent', bool: shutdownQueueStats.sessionStartEventPresent },
            { name: 'EditDocumentEventPresent', bool: shutdownQueueStats.editDocumentEventPresent },
            // Communication with webworker reliability stats
            { name: 'SentMessageCounter', double: stats.sentMessageCounter },
            { name: 'ReceivedMessageCounter', double: stats.receivedMessageCounter }
        ]
    };
    sendEventDirectlyToHandler({ kind: 'event', timestamp: new Date().getTime(), event: telemetryEvent });
}
function computeSessionAverages() {
    if (stats.processed > 0) {
        stats.averageProcessingTimeCostPerEvent = stats.eventsProcessingTime / stats.processed;
    }
    stats.sessionDuration = new Date().getTime() - sessionStartTime;
    stats.startOtelWorkerMethodDuration =
        stats.startOtelWorkerMethodScriptSharpDuration + stats.startOtelWorkerMethodTypescriptDuration;
    // Tactical adjustment
    // So here is the thing - there will always be 1 more sentMessage than receivedMessage because the last message sent during shutdown
    // will never receieve the corresponding response for it. This is by design, but does make it look like there is a mismatch in number
    // of sent and received requests. So let's make our lives easier and only count messages that were sent BEFORE shutdown was called.
    // That said, there is always a possility of sent and received message mismatch since we don't control when the application gets to close,
    // but we expect the number of those cases to be small.
    if (stats.sentMessageCounter > 0) {
        stats.sentMessageCounter -= 1;
    }
}
//# sourceMappingURL=workerApi.js.map